/**
 * AURELIA — component-quantity.js
 * <quantity-input> — enhances a native number input with stepper buttons.
 *
 * The input remains the source of truth: buttons drive it through the native
 * stepUp/stepDown API so browser validation, quantity rules (min/max/step) and
 * form submission all behave exactly as they would without JavaScript.
 *
 * On a cart line the change is debounced and dispatched to Aurelia.cart, which
 * serialises it against every other cart mutation.
 */
(function () {
  'use strict';

  const { Element, cart, utils, a11y, config } = window.Aurelia;

  class QuantityInput extends Element {
    mount() {
      this.input = this.querySelector('.quantity__input');
      if (!this.input) return;

      this.buttons = this.$$('[data-quantity-step]');
      this.lineKey = this.input.dataset.lineKey;
      this.lineIndex = this.input.dataset.lineIndex;
      this.lastValid = this.input.value;

      this.buttons.forEach((button) => {
        this.listen(button, 'click', () => this.step(Number(button.dataset.quantityStep)));
      });

      this.listen(this.input, 'change', () => this.commit());

      // Enter inside a quantity field should apply the change, not submit the
      // surrounding product form with a half-typed value.
      this.listen(this.input, 'keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.input.blur();
      });

      this.debouncedCartUpdate = utils.debounce(() => this.updateCartLine(), 450);
      this.syncButtons();
    }

    unmount() {
      if (this.debouncedCartUpdate) this.debouncedCartUpdate.cancel();
    }

    get min() {
      return Number(this.input.min) || 0;
    }

    get max() {
      return this.input.max === '' ? Infinity : Number(this.input.max);
    }

    step(direction) {
      if (direction > 0) {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }
      this.commit();
    }

    /** Clamps, announces, and propagates the value. */
    commit() {
      const parsed = parseInt(this.input.value, 10);
      let value = Number.isNaN(parsed) ? this.min : parsed;

      if (value > this.max) {
        value = this.max;
        if (Number.isFinite(this.max)) {
          utils
            .interpolate(config.strings.quantityError, { quantity: this.max })
            .then((message) => a11y.announce(message));
        }
      }

      // On a cart line, stepping below the minimum means "remove this item" —
      // that is what customers expect from a stepper next to a line item.
      if (value < this.min) value = this.lineKey ? 0 : this.min;

      this.input.value = value;
      this.syncButtons();

      if (String(value) === String(this.lastValid)) return;
      this.lastValid = value;

      this.input.dispatchEvent(new CustomEvent('quantity:change', { bubbles: true, detail: { value } }));

      if (this.lineKey || this.lineIndex) this.debouncedCartUpdate();
    }

    syncButtons() {
      const value = Number(this.input.value);
      this.buttons.forEach((button) => {
        const direction = Number(button.dataset.quantityStep);
        const atFloor = direction < 0 && value <= this.min && !this.lineKey;
        const atCeiling = direction > 0 && value >= this.max;
        button.disabled = atFloor || atCeiling;
      });
    }

    async updateCartLine() {
      const quantity = Number(this.input.value);
      this.setBusy(true);

      try {
        await cart.change(
          this.lineKey ? { id: this.lineKey, quantity } : { line: Number(this.lineIndex), quantity }
        );
      } catch (error) {
        // Restore the server's value: the cart is authoritative, not the input.
        const state = cart.state;
        const line = state && state.items.find((item) => item.key === this.lineKey);
        this.input.value = line ? line.quantity : this.min;
        this.lastValid = this.input.value;
        a11y.announce(error.message || config.strings.cartError, true);
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);
      this.buttons.forEach((button) => {
        button.disabled = isBusy;
      });
      if (!isBusy) this.syncButtons();
    }
  }

  window.Aurelia.define('quantity-input', QuantityInput);
})();
